import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  fetchFormAttendees,
  fetchFormInfo,
  publicFormErrorMessage,
  PublicFormError,
  type FormSession,
  type PublicAttendee,
  type PublicFormInfo,
  type SubmissionReceipt,
} from '@/lib/publicForm'
import { brandThemeStyle, buildBrandTheme } from '@/lib/brandTheme'
import { PasscodeGate } from '@/features/publicForm/PasscodeGate'
import { MatchmakingForm } from '@/features/publicForm/MatchmakingForm'

/**
 * The public matchmaking form at /f/:slug.
 *
 * No Supabase session is involved: everything goes through the `form-access`
 * Edge Function, gated by the shared event passcode. The page is built for a
 * phone held one-handed in a noisy room — see src/styles/index.css.
 */
export function PublicFormPage() {
  const { slug = '' } = useParams<{ slug: string }>()

  const [form, setForm] = useState<PublicFormInfo | null>(null)
  const [session, setSession] = useState<FormSession | null>(null)
  const [attendees, setAttendees] = useState<PublicAttendee[] | null>(null)
  const [receipt, setReceipt] = useState<SubmissionReceipt | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [expiredNotice, setExpiredNotice] = useState<string | undefined>(undefined)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    void fetchFormInfo(slug)
      .then((info) => {
        if (!cancelled) setForm(info)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        // Only the curated messages reach an attendee; anything else is an
        // internal string that would mean nothing to them.
        setLoadError(
          err instanceof PublicFormError
            ? publicFormErrorMessage(err.code)
            : 'This form could not be loaded.'
        )
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [slug])

  const handleUnlocked = useCallback(async (nextSession: FormSession) => {
    setExpiredNotice(undefined)
    try {
      setAttendees(await fetchFormAttendees(nextSession))
      setSession(nextSession)
    } catch (err) {
      setLoadError(
        err instanceof PublicFormError
          ? publicFormErrorMessage(err.code)
          : 'The attendee list could not be loaded.'
      )
    }
  }, [])

  const handleSessionExpired = useCallback(() => {
    // The draft is kept, so re-entering the passcode returns them to their picks.
    setSession(null)
    setExpiredNotice('Your session timed out. Enter the passcode again — your picks are saved.')
  }, [])

  // A brand colour, if the organiser set one, with contrast enforced.
  const themeStyle = brandThemeStyle(buildBrandTheme(form?.brand_color))

  if (loading) {
    return (
      <main className="attendee">
        <div className="attendee-page">
          <p className="page-status">Loading…</p>
        </div>
      </main>
    )
  }

  if (loadError !== null || form === null) {
    return (
      <main className="attendee">
        <div className="attendee-page">
          <div className="attendee-card">
            <p role="alert">{loadError ?? 'This form could not be loaded.'}</p>
          </div>
        </div>
      </main>
    )
  }

  return (
    <main className="attendee" style={themeStyle}>
      <div className="attendee-page">
        <header className="attendee-header">
          {form.logo_url !== null && (
            <img className="attendee-logo" src={form.logo_url} alt={form.organization_name} />
          )}
          <p className="attendee-eyebrow">{form.organization_name}</p>
          <h1 className="attendee-title">{form.title}</h1>
          {form.intro_text !== null && <p className="attendee-intro">{form.intro_text}</p>}
        </header>

        {receipt !== null ? (
          <div className="attendee-card">
            <div className="attendee-success">
              <div className="attendee-success-mark" aria-hidden="true">
                ✓
              </div>
              <h2>You&rsquo;re all set</h2>
              <p>
                {receipt.closing_message ??
                  'Thanks for coming. If anyone you picked picked you back, we will email you their details.'}
              </p>
              {receipt.previous_submissions > 0 && (
                <p className="field-hint">
                  You had already sent{' '}
                  {receipt.previous_submissions === 1
                    ? 'an answer'
                    : `${String(receipt.previous_submissions)} answers`}{' '}
                  tonight. This latest one is the one that counts.
                </p>
              )}
            </div>
          </div>
        ) : session === null || attendees === null ? (
          <PasscodeGate
            form={form}
            onUnlocked={(s) => void handleUnlocked(s)}
            reason={expiredNotice}
          />
        ) : (
          <MatchmakingForm
            form={form}
            session={session}
            attendees={attendees}
            onSubmitted={setReceipt}
            onSessionExpired={handleSessionExpired}
          />
        )}

        <footer className="attendee-footer">
          <p>{form.event_name}</p>
        </footer>
      </div>
    </main>
  )
}
